// The merchant's Pricing tab: what the product costs, once — an amount and
// its currency. The state and the save are the provider's (./pricing-state.jsx).
import React from "react"
import Typography from "aldoo-ra/Typography"
import { formatMoney } from "aldoo-ra/Store/currency"
import { currencyOptions } from "aldoo-ra/Store/currencies"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import { Field, TextInput, Select } from "../../../AdminShared/ui"
import { Alert } from "../shared/shared"
import { ONE_TIME_PURCHASE, amountText, useVendorPricing } from "./pricing-state"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

const Notice = ({ children }) => <div className="text-admin_text_secondary text-sm p-2">{children}</div>

export default function VendorPricingTab() {
  const t = useProductEditorTexts()
  const pricing = useVendorPricing()
  if (!pricing) return <Notice>{t`This product has no catalog document yet.`}</Notice>
  if (pricing.error) return <Notice>{pricing.error}</Notice>
  if (!pricing.ready) return <Notice>{t`Loading…`}</Notice>

  const { amount, currency, currencies, saved, priceError, productType, change } = pricing
  return (
    <div className="flex flex-col gap-4 max-w-xl" data-testid="vendor-pricing">
      <div>
        <Typography component="h4" variant="admin_h4" className="my-0 mb-2 inline-flex items-center gap-1.5">{t`Price`}<HelpHint helpKey="vendor.product#vendor-pricing" /></Typography>
        <p className="text-sm text-admin_text_secondary my-0">{t`What a buyer pays for the product, once.`}</p>
      </div>

      {productType !== ONE_TIME_PURCHASE && (
        <div data-testid="vendor-pricing-type-note">
          <Alert severity="info">{t`This product is set up to be sold another way (a bundle or a subscription). Once you save, it is sold as a one-time purchase.`}</Alert>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 md:items-start">
        <Field label={`${t`Price`} *`} error={priceError} className="md:w-48">
          <TextInput
            inputMode="decimal"
            autoComplete="off"
            placeholder="0.00"
            value={amount}
            onChange={(text) => change({ amount: text.replace(/[^\d.,]/g, "") })}
            // written out as money once the merchant moves on: 12,5 → 12.50
            onBlur={() => {
              if (priceError) return
              const formatted = amountText(Math.round(Number(String(amount).replace(",", ".")) * 100))
              if (formatted !== amount) change({ amount: formatted })
            }}
            className="tabular-nums text-right"
            data-testid="vendor-price-amount"
            aria-invalid={!!priceError}
          />
        </Field>
        <Field label={t`Currency`} className="md:w-40">
          <Select
            value={currency}
            onChange={(code) => code && change({ currency: code })}
            options={currencyOptions({ currencies })}
            className="w-full"
            data-testid="vendor-price-currency"
          />
        </Field>
      </div>

      <div className="text-sm text-admin_text_secondary" data-testid="vendor-price-current">
        {saved && saved.cents > 0 ? (
          <>
            {t`Current price`}: <span className="font-medium text-admin_text tabular-nums">{formatMoney(saved.cents, saved.currency, { cents: true })}</span> · {t`one-time purchase`}
          </>
        ) : (
          t`No price yet: the product cannot be sold until it has one.`
        )}
      </div>
    </div>
  )
}
